// ============= Imports =============
import { createRelayHubLog } from '../shared/utils';
import type { RelayHubHandlers } from '../shared';
import { UserRelayHubEvents, UserRelayHubPresenceMessageType } from './events';
import type {
  UserRelayPresenceHandler,
  UserRelayReceiveMessageHandler,
  UserRelayWorkspaceEventHandler,
} from './types';
import type { UserRelayHubOptions } from './types';
import {
  createUserEventHandler,
  isRecord,
  normalizeWorkspaceEventArgs,
  parseUserPresenceArgs,
  parseUserReceiveMessageArgs,
  type UserEventHandlerContext,
} from './utils';
import {
  getUserDisplayNameFromRecord,
  type UserNameLike,
} from '@/utils/userDisplayName';
import { sendMessageToUserUserSession } from '@/apis/core/messaging/userMessageRelayHub/post/sendMessageToUserUserSession';

// ============= Handler Factory =============

/**
 * Creates handlers for all supported User relay hub events and provides
 * subscribe/unsubscribe helpers.
 */
export const createUserRelayHubHandlers = (
  handlers: RelayHubHandlers,
  options: UserRelayHubOptions = {},
) => {
  const {
    workspaceId,
    onDataRefresh,
    onMessageReceived,
    onEventReceived,
    toasts,
    enableToasts = true,
    enableLogging = true,
    getDisplayNameForUser: getDisplayNameFromState,
    invokeHub,
    currentUserId,
    currentWorkspaceId,
    onPresenceResponse,
  } = options;

  const log = createRelayHubLog('UserRelayHub', enableLogging, '👤');

  const { showSuccess, showError, showInfo, showWarning } =
    enableToasts && toasts
      ? toasts
      : {
          showSuccess: () => {},
          showError: () => {},
          showInfo: () => {},
          showWarning: () => {},
        };

  const ctx: UserEventHandlerContext = {
    workspaceId,
    log,
    onEventReceived,
    onMessageReceived,
    onDataRefresh,
    enableToasts,
    showSuccess,
    showError,
    showInfo,
    showWarning,
  };

  const handleWorkspaceAdded: UserRelayWorkspaceEventHandler = (
    param1,
    workspaceIdParam,
    workspaceData,
  ) => {
    const { actualWorkspaceId, actualWorkspaceData, workspaceName, parsed } =
      normalizeWorkspaceEventArgs(param1, workspaceIdParam, workspaceData);

    if (workspaceId && actualWorkspaceId !== workspaceId) return;

    log(UserRelayHubEvents.WORKSPACE_ADDED, {
      param1,
      workspaceIdParam,
      workspaceData,
      actualWorkspaceId,
      actualWorkspaceData,
      workspaceName,
      workspaceIdFilter: workspaceId,
      willShowToast: workspaceId ? actualWorkspaceId === workspaceId : true,
    });

    if (onEventReceived) {
      onEventReceived(
        UserRelayHubEvents.WORKSPACE_ADDED,
        {
          param1,
          workspaceId: actualWorkspaceId,
          workspaceData: actualWorkspaceData,
        },
        { ...parsed },
      );
    }

    if (onMessageReceived) {
      onMessageReceived({
        event: UserRelayHubEvents.WORKSPACE_ADDED,
        workspaceId: actualWorkspaceId,
        workspaceData: actualWorkspaceData,
        parsed,
      });
    }

    if (enableToasts) {
      showSuccess(`Workspace "${workspaceName}" has been added`);
    }

    if (onDataRefresh) void onDataRefresh();
  };

  const handleUserAddedToWorkspace = createUserEventHandler(ctx, {
    event: UserRelayHubEvents.USER_ADDED_TO_WORKSPACE,
    toastType: 'info',
    getToastMessage: (p) => `${p.userName} has been added to the workspace`,
  });

  const handleUserDeletedFromWorkspace = createUserEventHandler(ctx, {
    event: UserRelayHubEvents.USER_DELETED_FROM_WORKSPACE,
    toastType: 'info',
    getToastMessage: (p) => `${p.userName} has been removed from the workspace`,
  });

  const handleUserPromotedToOwner = createUserEventHandler(ctx, {
    event: UserRelayHubEvents.USER_PROMOTED_TO_OWNER_IN_WORKSPACE,
    toastType: 'success',
    getToastMessage: (p) => `${p.userName} has been promoted to owner`,
  });

  const handleUserDemotedFromOwner = createUserEventHandler(ctx, {
    event: UserRelayHubEvents.USER_DEMOTED_FROM_OWNER_IN_WORKSPACE,
    toastType: 'info',
    getToastMessage: (p) => `${p.userName} has been demoted from owner`,
  });

  const handleUserReactivatedInWorkspace = createUserEventHandler(ctx, {
    event: UserRelayHubEvents.USER_REACTIVATED_IN_WORKSPACE,
    toastType: 'success',
    getToastMessage: (p) =>
      `${p.userName} has been reactivated in the workspace`,
  });

  const handleWorkspaceUpdated: UserRelayWorkspaceEventHandler = (
    param1,
    workspaceIdParam,
    workspaceData,
  ) => {
    const { actualWorkspaceId, actualWorkspaceData, workspaceName, parsed } =
      normalizeWorkspaceEventArgs(param1, workspaceIdParam, workspaceData);

    if (workspaceId && actualWorkspaceId !== workspaceId) return;

    log(UserRelayHubEvents.WORKSPACE_UPDATED, {
      param1,
      workspaceIdParam,
      workspaceData,
      actualWorkspaceId,
      actualWorkspaceData,
      workspaceName,
    });

    if (onEventReceived) {
      onEventReceived(
        UserRelayHubEvents.WORKSPACE_UPDATED,
        {
          param1,
          workspaceId: actualWorkspaceId,
          workspaceData: actualWorkspaceData,
        },
        { ...parsed },
      );
    }

    if (onMessageReceived) {
      onMessageReceived({
        event: UserRelayHubEvents.WORKSPACE_UPDATED,
        workspaceId: actualWorkspaceId,
        workspaceData: actualWorkspaceData,
        parsed,
      });
    }

    if (enableToasts) {
      showInfo(`Workspace "${workspaceName}" has been updated`);
    }

    if (onDataRefresh) void onDataRefresh();
  };

  const getUserDisplayName = (
    userData: unknown,
    userId: string,
    workspaceIdParam?: string,
  ): string => {
    if (getDisplayNameFromState && userId) {
      const fromState = getDisplayNameFromState(userId, workspaceIdParam);
      if (fromState && fromState !== userId) return fromState;
    }
    return getUserDisplayNameFromRecord(
      isRecord(userData) ? (userData as UserNameLike) : undefined,
    );
  };

  const handleUserPresenceEvent = (
    event: UserRelayHubEvents.USER_ONLINE | UserRelayHubEvents.USER_OFFLINE,
    args: unknown[],
    toastMessage: (displayName: string) => string,
  ): void => {
    const { workspaceIdParam, userIdParam, userData } =
      parseUserPresenceArgs(args);
    const displayName = getUserDisplayName(
      userData,
      userIdParam,
      workspaceIdParam,
    );

    if (workspaceId) {
      if (workspaceIdParam === undefined || workspaceIdParam !== workspaceId) {
        return;
      }
    } else {
      return;
    }

    const parsed = {
      workspaceId: workspaceIdParam,
      userId: userIdParam,
      userData: userData ?? null,
      displayName,
    };

    log(
      event,
      { workspaceIdParam, userIdParam, displayName, parsed },
      { workspaceId: workspaceIdParam, displayName },
    );

    if (onEventReceived) {
      onEventReceived(
        event,
        { workspaceIdParam, userIdParam, userData },
        { ...parsed },
      );
    }

    if (enableToasts && (!currentUserId || userIdParam !== currentUserId)) {
      showInfo(toastMessage(displayName));
    }
  };

  const handleUserOnline: UserRelayPresenceHandler = (...args) => {
    handleUserPresenceEvent(
      UserRelayHubEvents.USER_ONLINE,
      args,
      (displayName) => `${displayName} has come online`,
    );
  };

  const handleUserOffline: UserRelayPresenceHandler = (...args) => {
    handleUserPresenceEvent(
      UserRelayHubEvents.USER_OFFLINE,
      args,
      (displayName) => `${displayName} has gone offline`,
    );
  };

  const handleReceiveMessage: UserRelayReceiveMessageHandler = (...args) => {
    const { senderId, message } = parseUserReceiveMessageArgs(args);
    const messageType = message.type;
    const workspaceIdFromMessage =
      typeof message.workspaceId === 'string'
        ? message.workspaceId
        : undefined;

    if (messageType === UserRelayHubPresenceMessageType.ARE_YOU_THERE) {
      if (invokeHub && currentUserId) {
        void sendMessageToUserUserSession({
          invokeHub,
          userId: senderId ?? 'unknown',
          senderId: currentUserId,
          message: {
            type: UserRelayHubPresenceMessageType.I_AM_HERE,
            workspaceId: currentWorkspaceId ?? workspaceIdFromMessage,
          },
        });
        log(UserRelayHubEvents.RECEIVE_MESSAGE, {
          received: 'ARE_YOU_THERE',
          replied: 'I_AM_HERE',
          senderId,
        });
      }
      return;
    }

    if (messageType === UserRelayHubPresenceMessageType.I_AM_HERE) {
      if (onPresenceResponse && senderId) {
        onPresenceResponse(senderId, workspaceIdFromMessage);
        log(UserRelayHubEvents.RECEIVE_MESSAGE, {
          received: 'I_AM_HERE',
          senderId,
          workspaceId: workspaceIdFromMessage,
        });
      }
      if (onEventReceived) {
        onEventReceived(
          UserRelayHubEvents.RECEIVE_MESSAGE,
          { senderId, message },
          { senderId, workspaceId: workspaceIdFromMessage },
        );
      }
      if (onMessageReceived) {
        onMessageReceived({
          event: UserRelayHubEvents.RECEIVE_MESSAGE,
          senderId,
          message,
        });
      }
    }
  };

  type UserRegisteredHandler =
    | UserRelayWorkspaceEventHandler
    | ReturnType<typeof createUserEventHandler>
    | UserRelayPresenceHandler
    | UserRelayReceiveMessageHandler;

  type SignalRHandlerRegistration = Parameters<RelayHubHandlers['on']>[1];

  const eventHandlers: Array<[string, UserRegisteredHandler]> = [
    [UserRelayHubEvents.WORKSPACE_ADDED, handleWorkspaceAdded],
    [UserRelayHubEvents.USER_ADDED_TO_WORKSPACE, handleUserAddedToWorkspace],
    [
      UserRelayHubEvents.USER_DELETED_FROM_WORKSPACE,
      handleUserDeletedFromWorkspace,
    ],
    [
      UserRelayHubEvents.USER_PROMOTED_TO_OWNER_IN_WORKSPACE,
      handleUserPromotedToOwner,
    ],
    [
      UserRelayHubEvents.USER_DEMOTED_FROM_OWNER_IN_WORKSPACE,
      handleUserDemotedFromOwner,
    ],
    [
      UserRelayHubEvents.USER_REACTIVATED_IN_WORKSPACE,
      handleUserReactivatedInWorkspace,
    ],
    [UserRelayHubEvents.WORKSPACE_UPDATED, handleWorkspaceUpdated],
    [UserRelayHubEvents.USER_ONLINE, handleUserOnline],
    [UserRelayHubEvents.USER_OFFLINE, handleUserOffline],
    [UserRelayHubEvents.RECEIVE_MESSAGE, handleReceiveMessage],
  ];

  const subscribe = () => {
    eventHandlers.forEach(([event, handler]) =>
      handlers.on(event, handler as SignalRHandlerRegistration),
    );
  };

  const unsubscribe = () => {
    eventHandlers.forEach(([event, handler]) =>
      handlers.off(event, handler as SignalRHandlerRegistration),
    );
  };

  return {
    subscribe,
    unsubscribe,
    handlers: {
      handleWorkspaceAdded,
      handleUserAddedToWorkspace,
      handleUserDeletedFromWorkspace,
      handleUserPromotedToOwner,
      handleUserDemotedFromOwner,
      handleUserReactivatedInWorkspace,
      handleWorkspaceUpdated,
      handleUserOnline,
      handleUserOffline,
      handleReceiveMessage,
    },
  };
};
